import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Whether the site chrome — the header — should be on screen yet.
 *
 * The homepage opens on a full-bleed screen with no navigation, and the header
 * arrives only once the intro curtain has lifted and the hero's entrance has
 * finished. That is a decision the ROUTE makes and the HEADER obeys, and the
 * two are siblings under `SiteLayout` with no props between them, so it travels
 * through context rather than through five layers of drilling.
 *
 * DEFAULTS TO READY, and every consumer must keep it that way: any route that
 * does not opt out gets its header immediately, and a route that hides it is
 * responsible for restoring it on unmount. A navigation away from home while
 * the intro is mid-flight must not strand the rest of the site without
 * navigation — which is why `Index` restores on cleanup rather than on reveal.
 */
interface SiteChromeValue {
  ready: boolean;
  setReady: (ready: boolean) => void;
}

const SiteChromeContext = createContext<SiteChromeValue>({
  ready: true,
  setReady: () => undefined,
});

export function SiteChromeProvider({ children }: { children: ReactNode }) {
  const [ready, setReadyState] = useState(true);
  const setReady = useCallback((next: boolean) => setReadyState(next), []);
  const value = useMemo(() => ({ ready, setReady }), [ready, setReady]);

  return (
    <SiteChromeContext.Provider value={value}>
      {children}
    </SiteChromeContext.Provider>
  );
}

/** True when the header should be visible and interactive. */
export function useChromeReady(): boolean {
  return useContext(SiteChromeContext).ready;
}

/** For the route that owns the intro. */
export function useSetChromeReady(): (ready: boolean) => void {
  return useContext(SiteChromeContext).setReady;
}
